import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Lock, RefreshCw, Send, ShieldCheck, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import api from '../../services/api';
import { CertRoundsView } from './CertInboundStatus';

const STATE_STYLES = {
  NOT_READY: 'bg-slate-200 text-slate-700',
  READY_FOR_PREFLIGHT: 'bg-blue-100 text-blue-800',
  AWAITING_CONFIRMATION: 'bg-amber-100 text-amber-800',
  TRANSMISSION_LOCKED: 'bg-orange-100 text-orange-800',
  SENDING: 'bg-indigo-100 text-indigo-800',
  SENT: 'bg-emerald-100 text-emerald-800',
  AWAITING_RESPONSE: 'bg-indigo-100 text-indigo-800',
  TA1_RECEIVED: 'bg-emerald-100 text-emerald-800',
  '999_RECEIVED': 'bg-emerald-100 text-emerald-800',
  '271_RECEIVED': 'bg-emerald-100 text-emerald-800',
  REJECTED: 'bg-red-100 text-red-800',
  REQUIRES_CORRECTION: 'bg-red-100 text-red-800',
  COMPLETE: 'bg-emerald-200 text-emerald-900',
};

const GateRow = ({ gate }) => (
  <div className="flex items-start gap-2 py-1" data-testid={`cert-send-gate-${gate.id}`}>
    {gate.ok
      ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
      : <XCircle className="h-4 w-4 mt-0.5 text-red-500 shrink-0" />}
    <div className="min-w-0">
      <span className="text-sm text-slate-800">{gate.label}</span>
      {!gate.ok && gate.detail && (
        <p className="text-xs text-red-600 truncate" title={gate.detail}>{gate.detail}</p>
      )}
    </div>
  </div>
);

export const SendToCertPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phrase, setPhrase] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/cert-send/status');
      setStatus(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Send-to-CERT status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runPreflight = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/cert-send/preflight', {});
      setPreflight(data);
      if (data.ok) toast.success('CERT preflight PASSED');
      else toast.error(`Preflight failed: ${data.failing.join(', ')}`);
      await load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail : 'Preflight refused');
    } finally {
      setBusy(false);
    }
  };

  const confirmSend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/cert-send/confirm', {
        preflight_id: preflight?.id || status?.latest_preflight?.id,
        confirm_phrase: phrase,
      });
      if (data.state === 'manual_review') {
        toast.error(`Transmission requires review: ${data.failure_reason || 'transport not confirmed'}`);
      } else {
        toast.success(`Transmission ${data.state}`);
      }
      setConfirmOpen(false);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 423) {
        toast.warning(detail?.message || 'Transmission is LOCKED — nothing was sent.');
        setConfirmOpen(false);
        await load();
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Confirmation refused'));
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500" data-testid="cert-send-loading">Loading Send-to-CERT status…</div>;
  if (!status) return null;

  const state = status.state;
  const pf = preflight || null;
  const env = status.envelope || {};
  const summary = pf ? pf.envelope : env;
  const canPreflight = ['READY_FOR_PREFLIGHT', 'AWAITING_CONFIRMATION', 'NOT_READY'].includes(state);
  const canConfirm = state === 'AWAITING_CONFIRMATION' && (pf?.ok || status.latest_preflight?.ok);

  return (
    <div className="border rounded-lg p-5 space-y-4 bg-white" data-testid="send-to-cert-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-slate-700" />
          <h3 className="font-semibold text-slate-900">Send to CERT</h3>
          <Badge className={STATE_STYLES[state] || 'bg-slate-200'} data-testid="cert-send-state">
            {state.replaceAll('_', ' ')}
          </Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="cert-send-refresh-btn">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {!status.transmission_enabled && (
        <div className="flex items-center gap-2 rounded-md bg-orange-50 border border-orange-200 px-3 py-2"
             data-testid="cert-send-locked-banner">
          <Lock className="h-4 w-4 text-orange-600 shrink-0" />
          <p className="text-xs text-orange-800">
            TRANSMISSION LOCKED — ENABLE_CERT_TRANSMISSION=false. Preflight may run,
            but no file can be transmitted to ODM in this phase.
          </p>
        </div>
      )}

      {status.certification_progress && (
        <div className="flex items-center justify-between rounded-md bg-slate-50 border px-3 py-2"
             data-testid="cert-send-progress">
          <p className="text-sm font-medium text-slate-800">
            OMES CERT: submitted {status.rounds?.submitted ?? 0} of {status.certification_progress.rounds_required}
            {' · '}qualifying dates completed {status.certification_progress.rounds_completed} of{' '}
            {status.certification_progress.rounds_required}
          </p>
          <span className="text-xs text-slate-500">
            {status.certification_progress.completed_dates.join(' · ') || 'no qualifying dates yet'}
          </span>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-x-6" data-testid="cert-send-gate-list">
        {(status.gates || []).map((g) => <GateRow key={g.id} gate={g} />)}
      </div>

      <CertRoundsView rounds={status.rounds} inbound={status.inbound} />

      {status.failing?.length > 0 && (
        <div className="flex items-start gap-2 text-xs text-slate-600" data-testid="cert-send-unavailable-reason">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
          <span>SEND TO CERT unavailable: {status.failing.join(', ')}</span>
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        <Button onClick={runPreflight} disabled={busy || !canPreflight}
                variant="outline" data-testid="cert-send-preflight-btn">
          Run CERT Preflight
        </Button>
        <Button onClick={() => { setPhrase(''); setConfirmOpen(true); }}
                disabled={busy || !canConfirm}
                className="bg-slate-900 hover:bg-slate-700"
                data-testid="cert-send-open-confirm-btn">
          <Send className="h-4 w-4 mr-2" /> SEND TO CERT
        </Button>
      </div>

      {pf && (
        <div className="rounded-md border bg-slate-50 p-3 text-xs space-y-1" data-testid="cert-send-preflight-summary">
          <p className="font-medium text-slate-800">
            Preflight {pf.ok ? 'PASSED' : `FAILED (${pf.failing.join(', ')})`}
          </p>
          <p>File: {pf.file?.name} · SHA256 {pf.file?.sha256?.slice(0, 16)}…</p>
          <p>ISA13 {pf.envelope?.isa13} · GS06 {pf.envelope?.gs06} · ST02 {pf.envelope?.st02} · {pf.envelope?.st_count} transaction(s)</p>
          <p data-testid="cert-send-inquiry-count">
            Eligibility inquiries: {pf.envelope?.inquiry_count} (minimum required: {pf.envelope?.min_inquiries_required})
          </p>
          <p>Sender {pf.envelope?.sender_id} → Receiver {pf.envelope?.receiver_id} · Usage {pf.envelope?.usage_indicator}</p>
          <p>Destination: {pf.destination?.host} (certification)</p>
          <p>Expires {new Date(pf.expires_at).toLocaleTimeString()}</p>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="cert-send-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm CERT Transmission</DialogTitle>
            <DialogDescription>
              Second explicit authorization — review the batch identity below.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-1.5" data-testid="cert-send-confirm-summary">
            <p><span className="text-slate-500">Transactions:</span> {summary?.st_count}</p>
            <p><span className="text-slate-500">Eligibility inquiries:</span> {summary?.inquiry_count} (min {summary?.min_inquiries_required})</p>
            <p><span className="text-slate-500">Environment:</span> CERT (certification)</p>
            <p><span className="text-slate-500">Sender ID:</span> {summary?.sender_id}</p>
            <p><span className="text-slate-500">Receiver ID:</span> {summary?.receiver_id}</p>
            <p><span className="text-slate-500">ISA13 / GS06 / ST02:</span> {summary?.isa13} / {summary?.gs06} / {summary?.st02}</p>
            <p><span className="text-slate-500">Destination:</span> {(pf?.destination || status.destination)?.host}</p>
            <p><span className="text-slate-500">Validation:</span> {status.gates?.find((g) => g.id === 'x12_valid')?.ok ? 'PASSED' : 'FAILED'}</p>
            <p><span className="text-slate-500">Duplicate check:</span> {status.gates?.find((g) => g.id === 'duplicate_check')?.ok ? 'CLEAR' : 'DUPLICATE DETECTED'}</p>
            {!status.transmission_enabled && (
              <p className="text-orange-700 font-medium">Transmission is LOCKED — confirming will record the attempt but nothing will be sent.</p>
            )}
          </div>
          <div className="space-y-2">
            <p className="text-xs text-slate-500">Type <span className="font-mono font-semibold">SEND TO CERT</span> to proceed:</p>
            <Input value={phrase} onChange={(e) => setPhrase(e.target.value)}
                   placeholder="SEND TO CERT" data-testid="cert-send-confirm-phrase-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="cert-send-cancel-btn">Cancel</Button>
            <Button onClick={confirmSend} disabled={busy || phrase.trim() !== 'SEND TO CERT'}
                    className="bg-red-700 hover:bg-red-800" data-testid="cert-send-confirm-btn">
              CONFIRM CERT TRANSMISSION
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SendToCertPanel;
